import { Controller } from "@hotwired/stimulus"

// Drag-to-reorder for the Dials board.
//
// Uses the HTML5 drag events rather than pointer math because the cards are a
// simple vertical/grid list and the browser's own drag image is exactly the
// affordance we want. The new order is applied to the DOM optimistically and
// then PATCHed — the server treats unknown ids as "ignore" rather than an
// error, so a stale tab costs a stale ordering, not a reverted drag.
export default class extends Controller {
  static values = { url: String }

  connect() {
    this.element.querySelectorAll(".dials-board-item").forEach((item) => {
      item.draggable = true
    })
    this.onDragStart = this.dragStart.bind(this)
    this.onDragOver = this.dragOver.bind(this)
    this.onDrop = this.drop.bind(this)
    this.onDragEnd = this.dragEnd.bind(this)

    this.element.addEventListener("dragstart", this.onDragStart)
    this.element.addEventListener("dragover", this.onDragOver)
    this.element.addEventListener("drop", this.onDrop)
    this.element.addEventListener("dragend", this.onDragEnd)
  }

  disconnect() {
    this.element.removeEventListener("dragstart", this.onDragStart)
    this.element.removeEventListener("dragover", this.onDragOver)
    this.element.removeEventListener("drop", this.onDrop)
    this.element.removeEventListener("dragend", this.onDragEnd)
  }

  dragStart(event) {
    const item = event.target.closest(".dials-board-item")
    if (!item) return
    // A drag that starts on the title link or inside the menu is the browser's
    // link-drag or a mis-click, not a reorder — let it be. Reordering starts
    // from the card's own surface (and from the menu's Move up / Move down,
    // which is the keyboard route).
    if (event.target.closest("a, button, input, .dropdown-menu")) {
      event.preventDefault()
      return
    }
    this.dragging = item
    item.classList.add("is-dragging")
    // Firefox will not start a drag without data set on the transfer.
    event.dataTransfer.effectAllowed = "move"
    event.dataTransfer.setData("text/plain", item.dataset.id || "")
  }

  dragOver(event) {
    if (!this.dragging) return
    const over = event.target.closest(".dials-board-item")
    if (!over || over === this.dragging) return
    event.preventDefault()

    // Insert before or after depending on which half of the target we're over,
    // so a card can be dropped at either end of a row.
    const box = over.getBoundingClientRect()
    const after = event.clientY - box.top > box.height / 2
    over.parentNode.insertBefore(this.dragging, after ? over.nextSibling : over)
  }

  drop(event) {
    if (!this.dragging) return
    event.preventDefault()
    this.persist()
  }

  dragEnd() {
    this.dragging?.classList.remove("is-dragging")
    this.dragging = null
  }

  persist() {
    const ids = [...this.element.querySelectorAll(".dials-board-item")].map((el) => el.dataset.id)
    const token = document.querySelector('meta[name="csrf-token"]')?.content

    fetch(this.urlValue, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": token || "",
        Accept: "application/json"
      },
      body: JSON.stringify({ ids })
    }).catch(() => {
      // The order is already applied in the DOM and the next load will show
      // the server's truth; a failed save is not worth an alert over a drag.
    })
  }
}
